import clsx from 'clsx';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { JaneJohnTask } from '@/api/janeJohn';
import { ProjectType } from '@/api/project';
import { ProjectImageRow, SpiritTaskItem, ProjectSchedule } from '@/api/spirit';
import acheevementIcon from '@/assets/icons/achievements_icon.svg';
import applyIcon from '@/assets/icons/apply_icon.svg';
import { ReactComponent as ArrowIcon } from '@/assets/icons/bx-chevron-left.svg';
import caseIcon from '@/assets/icons/case_icon.svg';
import myPageIcon from '@/assets/icons/mypage_icon.svg';
import rewardIcon from '@/assets/icons/reward_icon.svg';
import NoImage from '@/assets/images/NoImage.png';
import SmartPhone from '@/assets/images/SmartPhone.png';
import { Button } from '@/components/Elements';
import { addCommaAmount, defaultTime } from '@/utils/format';
import { getImageUrlIfRelative } from '@/utils/image';
import { snsIcon } from '@/utils/sns';

import { ProjectOverviewProps } from '../ProjectManagements/Modals/ProjectOverview';
import { ProjectRecruitingState } from '../ProjectManagements/Modals/ProjectRecruitingTitle';
import { DetailSettings } from '../ProjectManagements/Modals/PublicAttention';

import { PreviewDetail } from './detail';
import { PreviewSchedule } from './schedule';

export const convertFromSpirit = (
  recruit: ProjectRecruitingState,
  detailImages: ProjectImageRow[],
  conditions: [string, string][],
  overview: ProjectOverviewProps,
  isSecondaryUse: boolean,
  schedules: ProjectSchedule[],
  reward: number,
  setting: DetailSettings,
  tasks: SpiritTaskItem[]
): PreviewType => {
  const recruitmentSchedules = schedules.find((value) => value.label == 'インフルエンサー募集期間');
  const recruitmentResultSchedules = schedules.find((value) => value.label == '最終確認期間');
  const feedbackSchedules = schedules.find((value) => value.label == 'インフルエンサー');

  const preview: PreviewType = {
    mainImage: recruit.displayImage ?? '',
    detailImages: detailImages.map((value) => value.objectKey),
    SNSType: recruit.sns,
    applicationConditions: formatApplicationConditions(conditions),
    tags: overview.displayTags,
    description: overview.displayDescription,
    isSecondaryUse: isSecondaryUse,
    recruitmentStartedAt: recruitmentSchedules?.from ?? null,
    recruitmentEndedAt: recruitmentSchedules?.to ?? null,
    recruitmentResultPeriodStartedAt: recruitmentResultSchedules?.from ?? null,
    recruitmentResultPeriodEndedAt: recruitmentResultSchedules?.to ?? null,
    title: recruit.displayTitle,
    reward: reward,
    companyName: setting.companyName,
    precautions: setting.notice,
    projectType: ProjectType.Spirit,
    feedbackPeriodStartedAt: feedbackSchedules?.from ?? defaultTime,
    feedbackPeriodEndedAt: feedbackSchedules?.to ?? defaultTime,
    tasks: tasks,
  };
  return preview;
};

export const convertFromPrst = (
  recruit: ProjectRecruitingState,
  detailImages: ProjectImageRow[],
  overview: ProjectOverviewProps,
  schedules: ProjectSchedule[],
  reward: number,
  setting: DetailSettings
): PreviewType => {
  const recruitmentSchedules = schedules.find((value) => value.label == 'インフルエンサー募集期間');
  const recruitmentResultSchedules = schedules.find((value) => value.label == '最終確認期間');

  const contractFrom =
    overview.displayContractFrom === undefined || overview.displayContractFrom === ''
      ? null
      : overview.displayContractFrom;
  const contractTo =
    overview.displayContractTo === undefined || overview.displayContractTo === '' ? null : overview.displayContractTo;

  const preview: PreviewType = {
    mainImage: recruit.displayImage ?? '',
    detailImages: detailImages.map((value) => value.objectKey),
    SNSType: recruit.sns,
    tags: overview.displayTags,
    description: overview.displayDescription,
    recruitmentStartedAt: recruitmentSchedules?.from ?? null,
    recruitmentEndedAt: recruitmentSchedules?.to ?? null,
    recruitmentResultPeriodStartedAt: recruitmentResultSchedules?.from ?? null,
    recruitmentResultPeriodEndedAt: recruitmentResultSchedules?.to ?? null,
    title: recruit.displayTitle,
    reward: reward,
    companyName: setting.companyName,
    precautions: setting.notice,
    projectType: ProjectType.Prst,
    creatorImage: overview.displayCreatorImage ?? '',
    contractPeriodFrom: contractFrom,
    contractPeriodTo: contractTo,
  };
  return preview;
};

export const convertFromJaneJohn = (
  recruit: ProjectRecruitingState,
  detailImages: ProjectImageRow[],
  overview: ProjectOverviewProps,
  schedules: ProjectSchedule[],
  reward: number,
  setting: DetailSettings,
  task: JaneJohnTask
): PreviewType => {
  const recruitmentSchedules = schedules.find((value) => value.label == 'インフルエンサー募集期間');
  const recruitmentResultSchedules = schedules.find((value) => value.label == '最終確認期間');
  const feedbackSchedules = schedules.find((value) => value.label == 'フィードバック期間（インフルエンサー）');

  const preview: PreviewType = {
    mainImage: recruit.displayImage ?? '',
    detailImages: detailImages.map((value) => value.objectKey),
    SNSType: recruit.sns,
    tags: overview.displayTags,
    description: overview.displayDescription,
    recruitmentStartedAt: recruitmentSchedules?.from ?? null,
    recruitmentEndedAt: recruitmentSchedules?.to ?? null,
    recruitmentResultPeriodStartedAt: recruitmentResultSchedules?.from ?? null,
    recruitmentResultPeriodEndedAt: recruitmentResultSchedules?.to ?? null,
    feedbackPeriodStartedAt: feedbackSchedules?.from ?? null,
    feedbackPeriodEndedAt: feedbackSchedules?.to ?? null,
    title: recruit.displayTitle,
    reward: reward,
    companyName: setting.companyName,
    precautions: setting.notice,
    projectType: ProjectType.JaneJohn,
    task: task,
  };
  return preview;
};

export const convertFromOther = (
  recruit: ProjectRecruitingState,
  detailImages: ProjectImageRow[],
  overview: ProjectOverviewProps,
  schedules: ProjectSchedule[],
  reward: number,
  setting: DetailSettings
): PreviewType => {
  const recruitmentSchedules = schedules.find((value) => value.label == 'インフルエンサー募集期間');
  const recruitmentResultSchedules = schedules.find((value) => value.label == '最終確認期間');
  const feedbackSchedules = schedules.find((value) => value.label == 'フィードバック期間（インフルエンサー）');
  const mainImage = recruit.displayImage ? getImageUrlIfRelative(recruit.displayImage) : '';

  const preview: PreviewType = {
    mainImage: mainImage,
    detailImages: detailImages.map((value) => getImageUrlIfRelative(value.objectKey)),
    SNSType: recruit.sns,
    tags: overview.displayTags,
    description: overview.displayDescription,
    recruitmentStartedAt: recruitmentSchedules?.from ?? null,
    recruitmentEndedAt: recruitmentSchedules?.to ?? null,
    recruitmentResultPeriodStartedAt: recruitmentResultSchedules?.from ?? null,
    recruitmentResultPeriodEndedAt: recruitmentResultSchedules?.to ?? null,
    feedbackPeriodStartedAt: feedbackSchedules?.from ?? null,
    feedbackPeriodEndedAt: feedbackSchedules?.to ?? null,
    title: recruit.displayTitle,
    reward: reward,
    companyName: setting.companyName,
    precautions: setting.notice,
    projectType: ProjectType.Other,
  };
  return preview;
};

const formatApplicationConditions = (conditions: [string, string][]): string[] => {
  const noBlankConditions = conditions.filter(([, value]) => value !== '-' && value !== '指定なし');

  return noBlankConditions.reduce((pre: string[], next: [string, string]) => {
    const key = next[0];
    if (key == '・居住地') {
      const arr = next[1].split('、').map((val) => val.trim());
      return [...pre, ...arr];
    }
    return [...pre, next[1]];
  }, []);
};

type DisplayProjectType = Record<ProjectType, { text: string; css: string }>;
const displayProjectType: DisplayProjectType = {
  [ProjectType.Spirit]: {
    text: 'SPIRIT',
    css: 'bg-pink-100 text-pink-800',
  },
  [ProjectType.EmeraldPost]: {
    text: 'EMERALD POST',
    css: 'bg-[#D1FAE5] text-[#065F46]',
  },
  [ProjectType.Prst]: {
    text: 'PRST',
    css: 'bg-[#EDE9FE] text-[#5B21B6]',
  },
  [ProjectType.JaneJohn]: {
    text: 'JANEJOHN',
    css: 'bg-[#DBEAFE] text-[#1E40AF]',
  },
  [ProjectType.Other]: {
    text: 'OTHER',
    css: 'bg-[#F3F4F6] text-[#1F2937]',
  },
};
type PreviewType = {
  mainImage: string;
  title: string;
  description: string;
  reward: number;
  SNSType: string;
  companyName: string;
  detailImages: string[];
  tags: string[];
  precautions: string[];
  projectType: ProjectType;
  isSecondaryUse?: boolean;
  applicationConditions?: string[];
  recruitmentStartedAt: string | null;
  recruitmentEndedAt: string | null;
  recruitmentResultPeriodStartedAt: string | null;
  recruitmentResultPeriodEndedAt: string | null;
  feedbackPeriodStartedAt?: string | null;
  feedbackPeriodEndedAt?: string | null;
  tasks?: SpiritTaskItem[];
  creatorImage?: string;
  contractPeriodFrom?: string | null;
  contractPeriodTo?: string | null;
  task?: JaneJohnTask;
};

export const Preview = (props: PreviewType) => {
  return (
    <div className="relative">
      <img className=" w-[375px] pl-[40px] pt-[16px]" src={SmartPhone} alt="" />
      <div className="absolute left-[54px] top-[65px] h-[627px] w-[305px] overflow-y-auto overflow-x-hidden rounded-3xl bg-white py-0">
        <div className="sticky top-0 z-10 flex h-14 items-center bg-white px-4 shadow">
          <ArrowIcon
            className="absolute top-1/2 -translate-y-1/2"
            height={24}
            width={24}
            fill="#9CA3AF"
            onClick={() => {}}
          />
          <h3 className="flex-1 text-center font-semibold leading-7">募集中案件詳細</h3>
        </div>

        <div className="relative">
          <Swiper
            modules={[Pagination]}
            slidesPerView={1}
            pagination={{
              clickable: true,
            }}
          >
            <SwiperSlide>
              <div className="h-[201px] w-full overflow-hidden">
                <div className="mb-4 flex h-full w-full items-center justify-center">
                  <img
                    className="min-h-full min-w-full object-cover"
                    src={props.mainImage === '' ? NoImage : props.mainImage}
                    alt=""
                  />
                </div>
              </div>
            </SwiperSlide>
            {props.detailImages.length > 0 &&
              props.detailImages.map((value, index) => (
                <div key={index}>
                  <SwiperSlide>
                    <div className="h-[201px] w-full overflow-hidden">
                      <div className="flex h-full w-full items-center justify-center">
                        <img src={value} alt="" className="min-h-full min-w-full object-cover" />
                      </div>
                    </div>
                  </SwiperSlide>
                </div>
              ))}
          </Swiper>
        </div>
        <div className="px-4">
          <div className="mt-4">
            <span
              className={clsx(
                'mb-2 inline-flex items-center rounded px-2.5 py-0.5 text-xs font-medium',
                displayProjectType[props.projectType].css ? displayProjectType[props.projectType].css : ''
              )}
            >
              {displayProjectType[props.projectType].text}
            </span>
            <p className="mb-2 font-semibold leading-7">{props.title}</p>
            <div className="mb-[26px] flex h-7 justify-between">
              <div className="flex items-center gap-1">
                <span className="text-2xl font-bold">{addCommaAmount(props.reward)}</span>
                <span className="text-[#8E8E8E]">報酬額</span>
              </div>
              {props.projectType !== ProjectType.Other && (
                <div className="flex items-center">
                  <div>{snsIcon(props.SNSType, 'h-[24px] w-[24px]')}</div>
                </div>
              )}
            </div>
            <div className="mb-[25px] h-[1px] w-[273px] bg-gray-300" />
          </div>
        </div>
        {props.projectType !== ProjectType.EmeraldPost && <PreviewDetail {...props} />}
        {props.projectType !== ProjectType.EmeraldPost && <PreviewSchedule {...props} />}
        <div className=" mb-[26px] px-4">
          <h4 className="mb-2 text-lg font-semibold leading-7 ">注意事項</h4>
          {props.precautions.map((noticeString, index) => (
            <p className="text-sm" key={index}>
              {noticeString}
            </p>
          ))}
          <br />
          <p className="text-sm text-gray-500">&#x30FB;応募条件を満たしている場合のみ応募可能です。</p>
        </div>
        <div className="mb-[26px] px-4">
          <h4 className="mb-4 text-lg font-semibold leading-7 ">募集企業</h4>
          <p className="text-sm">{props.companyName}</p>
        </div>
        <div className="mb-[120px] flex w-full justify-center">
          <Button size="smNopadding" className="h-[42px] w-[273px] font-bold">
            応募する
          </Button>
        </div>
        <div className="flex h-14 justify-center border-t border-gray-300 pt-4 text-xs text-gray-500">
          <div className=" flex w-[75px] flex-col items-center">
            <img src={applyIcon} alt="" />
            <span className="text-blue-500">応募</span>
          </div>
          <div className="flex w-[75px] flex-col items-center">
            <img src={caseIcon} alt="" />
            <span>担当案件</span>
          </div>
          <div className="flex w-[75px] flex-col items-center">
            <img src={acheevementIcon} alt="" />
            <span>実績</span>
          </div>
          <div className="flex w-[75px] flex-col items-center">
            <img src={rewardIcon} alt="" />
            <span>報酬</span>
          </div>
          <div className="flex w-[75px] flex-col items-center">
            <img src={myPageIcon} alt="" />
            <span>マイページ</span>
          </div>
        </div>
      </div>
    </div>
  );
};
